import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
} from "recharts";
import { useResultsContext } from "../context/context";
import { TeamsWithColours } from "../RoundResultsPage";

interface Props {
  data: any[];
  teams: TeamsWithColours[];
  formatter?: (val: number) => string | number;
  maxYAxisvalue: number;
  minYAxisvalue: number;
  numberOfTicks: number;
  withoutCpuTeams?: boolean;
}

const createLegend = (val: any, colour: string) => {
  const Legend = (props: any) => {
    const { x, y, width, height, value } = props;
    if (value == null) {
      return null;
    }
    const smallScreen = window.matchMedia("(max-width: 820px)");
    const mediumScreen = window.matchMedia("(max-width: 2000px)");

    let yOffset;
    if (smallScreen.matches) {
      yOffset = 20;
    } else if (mediumScreen.matches) {
      yOffset = 25;
    } else {
      yOffset = 50;
    }
    return (
      <g>
        <text
          x={x + width / 2}
          y={y + height + yOffset}
          fill={colour}
          textAnchor="middle"
          fontWeight="bold"
          fontSize="clamp(0.25rem, calc(0.25rem + 0.80vw), 2rem)"
        >
          {val}
        </text>
      </g>
    );
  };
  return Legend;
};

const ResultsBarChart: React.FC<Props> = ({
  data,
  formatter,
  maxYAxisvalue,
  minYAxisvalue,
  numberOfTicks,
}) => {
  const largeScreen = window.matchMedia("(max-width: 3000px)");
  const mediumScreen = window.matchMedia("(max-width: 2000px)");
  const smallScreen = window.matchMedia("(max-width: 820px)");

  const getBarCategoryGap = () => {
    if (smallScreen.matches) return -50;
    else if (mediumScreen.matches) return -40;
    else if (largeScreen.matches) return -100;
    else return -10;
  };

  const getMaxBarSize = () => {
    if (smallScreen.matches) return -40;
    else if (mediumScreen.matches) return -60;
    else if (largeScreen.matches) return -25;
    else return -130;
  };

  const context = useResultsContext();

  return (
    <ResponsiveContainer width="100%" height="92%" debounce={1}>
      <BarChart
        data={data}
        margin={{
          top: 30,
          right: 50,
          bottom: 60,
        }}
        // hacky way of handling media query for bar width
        barCategoryGap={getBarCategoryGap()}
        maxBarSize={getMaxBarSize()}
      >
        <CartesianGrid vertical={false} />
        <ReferenceLine y={0} stroke="#000" />
        <XAxis
          dataKey="name"
          tickLine={false}
          padding={{ left: 75, right: 75 }}
          hide={true}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tickCount={numberOfTicks + 1}
          tickFormatter={formatter ? (tick) => formatter(tick) : (tick) => tick}
          domain={[minYAxisvalue ?? "auto", maxYAxisvalue ?? "auto"]}
          tick={{ fontSize: "clamp(0.15rem, calc(0.15rem + 0.35vw), 2rem)" }}
        />
        {data.map((d, idx) => {
          return (
            <Bar
              key={`bar-item-${idx}`}
              name={d.name}
              type="natural"
              dataKey={d.key}
              fill={d.colour}
              animationDuration={context.noAnimations ? 0 : 1000}
              animationBegin={context.noAnimations ? 0 : idx * 1000}
            >
              <LabelList position="top" content={d.topLabel}></LabelList>
              {d.hasMiddleLabel && (
                <LabelList position="middle" content={d.content}></LabelList>
              )}
              <LabelList
                position="bottom"
                content={createLegend(d.name, d.colour)}
              ></LabelList>
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ResultsBarChart;
